'use client';

import { algoliasearch } from 'algoliasearch';
import { parseCookies } from 'nookies';
import React from 'react';
import { Configure, ConfigureProps } from 'react-instantsearch';
import { InstantSearchNext } from 'react-instantsearch-nextjs';
import { useRecoilState } from 'recoil';

import Autocomplete from '@/app/_components/search/Autocomplete';
import { COOKIE_NAME_DEALERSHIP_ID } from '@/constants/dealership/dealership.constants';
import {
  PAGE_SIZE,
  USED_ALGOLIA_FILTER,
} from '@/constants/used-cars/used-cars.constants';
import { mustUseNewFiltersState } from '@/states/used-cars/filter-query.state';

import UsedCarHits from './UsedCarHits';
import { UsedCarInfiniteHits } from './UsedCarInfiniteHits';
import UsedCarPagination from './UsedCarPagination';

const algoliaClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_APP_ID || '',
  process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY || '',
);

// a proxy client which intercepts all search requests
const searchClient = {
  ...algoliaClient,
  search(requests: any) {
    if (requests.every(({ params }: { params: any }) => !params.query)) {
      // if there's an empty query, don't send search request, return empty result
      // this is to prevent an initial search when the search sidebar is opened
      return Promise.resolve({
        results: requests.map(() => ({
          hits: [],
          nbHits: 0,
          nbPages: 0,
          page: 0,
          processingTimeMS: 0,
          hitsPerPage: 0,
          exhaustiveNbHits: false,
          query: '',
          params: '',
        })),
      });
    }
    return algoliaClient.search(requests);
  },
};

const UsedCarSearch = ({ onSearch }: { onSearch?: () => void }) => {
  const cookies = parseCookies();
  const dealershipCookie = cookies[COOKIE_NAME_DEALERSHIP_ID];

  const filters = [USED_ALGOLIA_FILTER];

  if (dealershipCookie) {
    filters.push(` AND dealership_id.id:'${dealershipCookie}'`);
  }

  const isUsingNewFilters = useRecoilState(mustUseNewFiltersState);

  const configureProps: ConfigureProps = {
    index: 'used_cars',
    hitsPerPage: PAGE_SIZE,
    filters: filters.join(),
  } as ConfigureProps;

  return (
    <>
      <InstantSearchNext
        searchClient={searchClient as any}
        future={{ preserveSharedStateOnUnmount: true }}
      >
        <Configure {...configureProps} />
        <Autocomplete
          data-cy="main-search-box"
          placeholder="Search makes, models or keywords"
          detachedMediaQuery="none"
          openOnFocus
          onSearch={onSearch}
          insights={true}
        />
        {isUsingNewFilters ? (
          <UsedCarInfiniteHits />
        ) : (
          <>
            <UsedCarHits />
            <UsedCarPagination />
          </>
        )}
      </InstantSearchNext>
    </>
  );
};

export default UsedCarSearch;
